import { App } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiPost, errorMessage, qk } from "@/shared/api";

/**
 * Автомат заявки на вывод: `pending → approved → paid`, плюс отклонение.
 *
 * Три отдельные мутации, а не одна с параметром `action`: у каждой своё тело
 * запроса и свой набор обязательных полей, и «универсальная» мутация свелась
 * бы к тому же ветвлению, только внутри.
 */
export function useProcessWithdrawal() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  const invalidate = (userId: number) => {
    void queryClient.invalidateQueries({ queryKey: ["withdrawals"] });
    void queryClient.invalidateQueries({ queryKey: qk.dashboard() });
    void queryClient.invalidateQueries({ queryKey: ["audit"] });
    void queryClient.invalidateQueries({ queryKey: qk.user(userId) });
    void queryClient.invalidateQueries({ queryKey: ["users"] });
  };

  const onError = (error: unknown) => {
    void message.error(errorMessage(error));
  };

  const approve = useMutation({
    mutationFn: (input: { id: number; userId: number; comment?: string }) =>
      apiPost("/api/v1/admin/withdrawals/{withdrawal_id}/approve", {
        params: { withdrawal_id: input.id },
        body: input.comment ? { comment: input.comment } : {},
      }),
    onSuccess: (_, input) => {
      invalidate(input.userId);
    },
    onError,
  });

  const markPaid = useMutation({
    mutationFn: (input: { id: number; userId: number; tx_hash: string }) =>
      apiPost("/api/v1/admin/withdrawals/{withdrawal_id}/paid", {
        params: { withdrawal_id: input.id },
        body: { tx_hash: input.tx_hash },
      }),
    onSuccess: (_, input) => {
      invalidate(input.userId);
    },
    onError,
  });

  const reject = useMutation({
    mutationFn: (input: { id: number; userId: number; comment: string }) =>
      apiPost("/api/v1/admin/withdrawals/{withdrawal_id}/reject", {
        params: { withdrawal_id: input.id },
        body: { comment: input.comment },
      }),
    onSuccess: (_, input) => {
      invalidate(input.userId);
    },
    onError,
  });

  return { approve, markPaid, reject };
}
